/**
 * Auto-ensure CLARITY INSTALLATION AND COMMISSIONING on D-Tools JSON import.
 * Uses the existing CLARITY-SVC-IC catalogue identity — never creates a duplicate catalogue row.
 */
import {
  CLARITY_STANDARD_SERVICE_ITEMS,
  clarityStandardServiceBySku,
  matchClarityStandardServiceSku,
} from "./clarityServiceCatalogueItems";
import {
  EQUIPMENT_PRICING_METHOD_PERCENTAGE,
  isEquipmentPercentagePricingMethod,
} from "./equipmentPercentagePricing";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";
import { buildDtoolsProjectCatalogKey } from "./dtoolsProjectCatalogKey";

export const CLARITY_INSTALLATION_COMMISSIONING_SKU = "CLARITY-SVC-IC";
/** Default Portal installation charge: 3% of base hardware subtotal (before markup). */
export const CLARITY_INSTALLATION_DEFAULT_PERCENTAGE = 3;

export const CLARITY_IC_DEFINITION = clarityStandardServiceBySku(CLARITY_INSTALLATION_COMMISSIONING_SKU)!;

export function isClarityInstallationCommissioningLine(line: {
  manufacturer?: string | null;
  brand?: string | null;
  model?: string | null;
  name?: string | null;
  displayName?: string | null;
  sku?: string | null;
  catalogueKey?: string | null;
  catalogId?: string | null;
}): boolean {
  const sku =
    matchClarityStandardServiceSku({
      brand: line.brand ?? line.manufacturer,
      manufacturer: line.manufacturer,
      model: line.model,
      name: line.name,
      displayName: line.displayName,
      sku: line.sku ?? line.catalogId,
      catalogueKey: line.catalogueKey,
    }) ?? null;
  return sku === CLARITY_INSTALLATION_COMMISSIONING_SKU;
}

export function findClarityInstallationCommissioningLineIndex(
  lines: Array<{
    manufacturer?: string | null;
    brand?: string | null;
    model?: string | null;
    name?: string | null;
    displayName?: string | null;
    sku?: string | null;
    catalogueKey?: string | null;
    catalogId?: string | null;
  }>,
): number {
  return lines.findIndex((line) => isClarityInstallationCommissioningLine(line));
}

/**
 * True when staff intentionally changed IC away from the import default
 * (flat fee mode, or percentage ≠ 3%). Reimport must not reset these.
 */
export function isClarityInstallationUserOverride(line: {
  pricingMethod?: unknown;
  percentageOfEquipment?: number | null;
  unitPrice?: number | null;
  lineTotal?: number | null;
  clarityServiceUserOverride?: boolean | null;
}): boolean {
  if (line.clarityServiceUserOverride === true) return true;
  if (!isEquipmentPercentagePricingMethod(line.pricingMethod)) {
    // Flat fee (including $0 after intentional remove-to-zero) counts as override once present.
    return true;
  }
  const pct = Number(line.percentageOfEquipment);
  if (!Number.isFinite(pct)) return false;
  return Math.abs(pct - CLARITY_INSTALLATION_DEFAULT_PERCENTAGE) > 0.0001;
}

function buildDefaultIcStagingLine(sortOrder: number): DtoolsStagingLine {
  const def = CLARITY_IC_DEFINITION;
  const catalogueKey =
    def.catalogueKey ||
    buildDtoolsProjectCatalogKey({
      manufacturer: "Clarity",
      model: def.model,
      catalogId: def.sku,
      displayName: def.displayName,
    });
  return {
    id: `portal-clarity-ic-${CLARITY_INSTALLATION_COMMISSIONING_SKU}`,
    dtoolsItemId: `portal:${CLARITY_INSTALLATION_COMMISSIONING_SKU}`,
    manufacturer: "Clarity",
    model: def.model,
    displayName: def.displayName,
    displayNameManuallyEdited: false,
    category: "Services",
    subcategory: null,
    dtoolsDescription: def.description,
    sourceDtoolsDescription: def.description,
    sourceDtoolsDescriptionReliable: true,
    polishedDescription: def.description,
    quantity: 1,
    unitPrice: 0,
    lineTotal: 0,
    baseUnitPrice: 0,
    systemId: null,
    systemName: null,
    locationId: null,
    roomName: null,
    parentId: null,
    sortOrder,
    bucket: "service",
    imageUrl: null,
    projectCatalogueId: null,
    catalogueKey,
    catalogId: def.sku,
    componentId: null,
    isAccessory: false,
    mergedSources: [],
    pricingMethod: EQUIPMENT_PRICING_METHOD_PERCENTAGE,
    percentageOfEquipment: CLARITY_INSTALLATION_DEFAULT_PERCENTAGE,
    clarityServiceUserOverride: false,
    // Canonical portal service — linked by stable SKU / catalogueKey (CLARITY-SVC-IC).
    catalogMatchConfidence: "matched",
  };
}

/**
 * Ensure IC appears once on imported staging lines at 3% of base hardware.
 * Does not duplicate; does not reset intentional user overrides on reimport.
 */
export function ensureClarityInstallationCommissioningOnImport(
  lines: DtoolsStagingLine[],
): DtoolsStagingLine[] {
  const idx = findClarityInstallationCommissioningLineIndex(lines);
  if (idx >= 0) {
    const existing = lines[idx]!;
    if (isClarityInstallationUserOverride(existing)) {
      return lines;
    }
    // Refresh default 3% only when still on the import default shape.
    const next = [...lines];
    next[idx] = {
      ...existing,
      manufacturer: "Clarity",
      model: CLARITY_IC_DEFINITION.model,
      displayName: existing.displayName?.trim() || CLARITY_IC_DEFINITION.displayName,
      catalogId: existing.catalogId?.trim() || CLARITY_INSTALLATION_COMMISSIONING_SKU,
      catalogueKey: existing.catalogueKey || CLARITY_IC_DEFINITION.catalogueKey,
      bucket: "service",
      pricingMethod: EQUIPMENT_PRICING_METHOD_PERCENTAGE,
      percentageOfEquipment: CLARITY_INSTALLATION_DEFAULT_PERCENTAGE,
      unitPrice: 0,
      lineTotal: 0,
      quantity: 1,
      clarityServiceUserOverride: false,
      catalogMatchConfidence: existing.catalogMatchConfidence ?? "matched",
    };
    return next;
  }

  const maxSort = lines.reduce((m, l) => Math.max(m, Number(l.sortOrder) || 0), -1);
  return [...lines, buildDefaultIcStagingLine(maxSort + 1)];
}

/** Deduplicate any accidental IC duplicates — keep the first / override-preferring row. */
export function dedupeClarityInstallationCommissioningLines(
  lines: DtoolsStagingLine[],
): DtoolsStagingLine[] {
  const icIndexes: number[] = [];
  lines.forEach((line, i) => {
    if (isClarityInstallationCommissioningLine(line)) icIndexes.push(i);
  });
  if (icIndexes.length <= 1) return lines;

  let keep = icIndexes[0]!;
  for (const i of icIndexes) {
    if (isClarityInstallationUserOverride(lines[i]!)) {
      keep = i;
      break;
    }
  }
  return lines.filter((_, i) => !icIndexes.includes(i) || i === keep);
}

export function clarityInstallationCatalogueSeedDefaults(): {
  sku: string;
  pricingMethod: typeof EQUIPMENT_PRICING_METHOD_PERCENTAGE;
  defaultPercentage: number;
  catalogueKey: string;
} {
  const def = CLARITY_STANDARD_SERVICE_ITEMS.find((i) => i.sku === CLARITY_INSTALLATION_COMMISSIONING_SKU)!;
  return {
    sku: def.sku,
    pricingMethod: EQUIPMENT_PRICING_METHOD_PERCENTAGE,
    defaultPercentage: CLARITY_INSTALLATION_DEFAULT_PERCENTAGE,
    catalogueKey: def.catalogueKey,
  };
}
